import React, { useEffect, useState } from 'react';
import { useOptionalAuth } from '../../context/AuthContext';
import ConfirmDialog from '../ui/ConfirmDialog';
import { authHeaders } from '../inventory/InventoryPanel';

/* Inventory signers — who may approve, reject and sign in the inventory,
   per bank. Lives HERE by Kawi's 20 Jul decision: "who signs is governed
   from BARK admin, never self-served on the profile". The profile page
   shows the list read-only; this panel is the one place that changes it.
   Every change is a record in the inventory engine.

   Every call carries authHeaders() (audit, 6 Aug): without the token the
   whole panel would 401 the moment auth is enforced. */

const API = process.env.REACT_APP_INVENTORY_API || 'http://localhost:8091';

interface Props {
    adminName?: string;
}

function InventorySignersPanel({ adminName }: Props) {
    /* the acting admin's real name for the record (round 4) — in
       production the engine takes the token identity regardless; this
       matters with auth off. useOptionalAuth is the no-throw variant:
       the hook call stays unconditional and lint-clean (the try/catch
       shape failed the production build — the round-9 note was right). */
    const auth = useOptionalAuth();
    const actorName = adminName || auth?.userData?.name || auth?.userData?.email || 'BARK admin';
    const [insts, setInsts] = useState<{ id: string; name: string }[] | null>(null);
    const [sel, setSel] = useState('');
    const [signers, setSigners] = useState<string[] | null>(null);
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [done, setDone] = useState('');
    /* E-DLG-01 (round 7): removing a signer is destructive — it confirms
       through the one dialog, never fires on the bare click */
    const [removeFor, setRemoveFor] = useState<string | null>(null);

    useEffect(() => {
        fetch(`${API}/api/inventory/institutions`, { headers: authHeaders() })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
            .then(d => {
                setInsts(d.institutions || []);
                if ((d.institutions || []).length > 0) setSel(d.institutions[0].id);
            })
            .catch(() => setErr('could not reach the inventory engine'));
    }, []);

    useEffect(() => {
        if (!sel) return;
        setSigners(null);
        fetch(`${API}/api/inventory/institutions/${sel}/signers`, { headers: authHeaders() })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
            .then(d => setSigners(d.signers || []))
            .catch(() => setErr('could not read the signer list'));
    }, [sel]);

    const change = async (action: 'add' | 'remove', addr: string) => {
        setBusy(true); setErr(''); setDone('');
        const r = await fetch(`${API}/api/inventory/institutions/${sel}/signers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ email: addr, action, by: actorName }),
        }).catch(() => null);
        setBusy(false);
        if (!r || !r.ok) {
            setErr(r ? ((await r.json().catch(() => ({}))).detail || `${r.status}`) : 'network error');
            return;
        }
        setEmail('');
        /* the act is visible (S-05) and a failed refresh is named, never
           mistaken for an empty list (S-03) — round 4 */
        setDone(action === 'add' ? `${addr} added — on record` : `${addr} removed — on record`);
        const d = await fetch(`${API}/api/inventory/institutions/${sel}/signers`,
            { headers: authHeaders() }).then(x => x.json())
            .catch(() => null);
        if (d === null) { setErr('saved, but the list could not refresh — reload to see it'); return; }
        setSigners(d.signers || []);
    };

    if (err && insts === null) return <p className="text-sm text-fg-secondary">{err}</p>;
    if (insts === null) return <p className="text-sm text-fg-secondary">Loading…</p>;

    return (
        <div className="max-w-xl">
            <p className="text-sm text-fg-secondary mb-4">
                Who may approve, reject and sign in the regulatory inventory — per bank.
                While a bank's list is empty, the global BARK list signs. Every change
                is written to the bank's record.
            </p>
            <label className="block text-sm font-medium text-fg mb-1">Bank</label>
            <select value={sel} onChange={e => setSel(e.target.value)}
                className="border border-border-default rounded-md px-3 py-2 text-sm mb-4 bg-surface text-fg">
                {insts.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>

            {signers === null && sel && <p className="text-sm text-fg-secondary">Loading the list…</p>}
            {signers !== null && (
                <div className="border border-border-default rounded-md divide-y divide-border-default mb-4">
                    {signers.length === 0 && (
                        <p className="px-3 py-2.5 text-sm text-fg-secondary">
                            No named signers yet — the global BARK list signs.</p>
                    )}
                    {signers.map(s => (
                        <div key={s} className="flex items-center px-3 py-2.5 text-sm">
                            <span className="text-fg">{s}</span>
                            <button onClick={() => setRemoveFor(s)} disabled={busy}
                                className="ml-auto text-xs font-semibold text-fg-secondary hover:underline disabled:opacity-50">
                                remove</button>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex gap-2 items-center">
                <input value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="name@bank.se"
                    className="border border-border-default rounded-md px-3 py-2 text-sm w-64 bg-surface text-fg" />
                <button onClick={() => change('add', email.trim())}
                    disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
                    className="px-3 py-2 rounded-md bg-accent text-white text-sm font-medium disabled:opacity-50">
                    {busy ? 'Saving…' : 'Add signer'}</button>
            </div>
            <ConfirmDialog open={removeFor !== null}
                onCancel={() => setRemoveFor(null)}
                onConfirm={async () => {
                    const addr = removeFor!;
                    setRemoveFor(null);
                    await change('remove', addr);
                }}
                title="Remove this signer"
                description={removeFor
                    ? `${removeFor} will no longer be able to approve, reject or sign for this bank. The removal is written to the bank's record.`
                    : ''}
                confirmLabel={`Remove — as ${actorName}`}
                variant="destructive" submitting={busy} />
            {done && !err && <p className="text-xs font-semibold text-fg-secondary mt-2">{done} ✓</p>}
            {err && <p className="text-xs text-danger mt-2">{err}</p>}
        </div>
    );
}

export default InventorySignersPanel;
